// ComplexOrderDetailsScreen.tsx
import React from "react";
import { useParams } from "react-router-dom";
import { useSelector } from "react-redux";
import { format } from "date-fns";
import { ru } from "date-fns/locale";
import { MdRepeat, MdErrorOutline, MdPhone, MdMessage } from "react-icons/md";
import { useTheme } from "../../../common/hooks/useTheme";
import { selectComplexOrderById } from "../../../store/slices/complexOrderListSlice";
import { useComplexRepeatOrder } from "../useComplexRepeatOrder";
import { useComplexOrderDetails } from "./useComplexOrderDetails";
import { MobileHeader } from "../../../common/components/mobileHeader/MobileHeader";
import { ProductHeader } from "../../../common/components/productHeader/ProductHeader";
import { EmptyState } from "../../../common/components/emptyState/EmptyState";
import styles from "./ComplexOrderDetailsScreen.module.scss";
import { PageMeta } from "../../../common/components/pageMeta/PageMeta";

export default function ComplexOrderDetailsScreen() {
  const { id } = useParams<{ id: string }>();
  const { theme } = useTheme();
  const order = useSelector(selectComplexOrderById(Number(id)));
  const { repeatOrder } = useComplexRepeatOrder();
  const { getOrderStatusInfo, getFieldName } = useComplexOrderDetails(order);

  const handleRepeat = () => {
    if (order) {
      repeatOrder(order);
    }
  };

  const statusInfo = order ? getOrderStatusInfo(order.status) : null;

  if (!order) {
    return (
      <div
        className={styles.container}
        style={{ backgroundColor: theme.background }}
      >
        <PageMeta customTitle="Заявка не найдена" />
        <div className={styles.mobileOnly}>
          <MobileHeader title="Детали заявки" />
        </div>
        <div className={styles.desktopOnly}>
          <ProductHeader
            title="Детали заявки"
            isFavorite={false}
            showFavorite={false}
            textColor={theme.text}
            borderColor={theme.border}
          />
        </div>
        <EmptyState
          icon={MdErrorOutline}
          title="Заявка не найдена"
          description={`Заявка №${id} не найдена`}
        />
      </div>
    );
  }

  const subtitle = order.createdAt
    ? `Заявка №${order.id} • ${format(new Date(order.createdAt), "dd.MM.yyyy HH:mm", { locale: ru })}`
    : undefined;

  return (
    <div
      className={styles.container}
      style={{ backgroundColor: theme.background }}
    >
      <PageMeta />

      <div className={styles.mobileOnly}>
        <MobileHeader
          title="Детали заявки"
          subtitle={subtitle}
          rightIcon={<MdRepeat size={24} color={theme.onPrimary} />}
          onRightClick={handleRepeat}
        />
      </div>

      <div className={styles.desktopOnly}>
        <ProductHeader
          title={`Детали заявки №${order.id}`}
          isFavorite={false}
          showFavorite={false}
          textColor={theme.text}
          borderColor={theme.border}
        />
      </div>

      <div className={styles.content}>
        <div
          className={styles.section}
          style={{ backgroundColor: theme.card, borderColor: theme.border }}
        >
          <h2 className={styles.sectionTitle} style={{ color: theme.text }}>
            Контактные данные
          </h2>
          <div className={styles.infoRow}>
            <span
              className={styles.infoLabel}
              style={{ color: theme.textSecondary }}
            >
              Телефон:
            </span>
            <div className={styles.infoValueWithIcon}>
              <MdPhone size={16} color={theme.primary} />
              <span className={styles.infoValue} style={{ color: theme.text }}>
                {order.phone_number || "Не указан"}
              </span>
            </div>
          </div>
          {order.notes && (
            <div className={styles.commentBlock}>
              <div className={styles.infoValueWithIcon}>
                <MdMessage size={16} color={theme.primary} />
                <span
                  className={styles.commentText}
                  style={{ color: theme.text }}
                >
                  {order.notes}
                </span>
              </div>
            </div>
          )}
        </div>

        <div
          className={styles.section}
          style={{ backgroundColor: theme.card, borderColor: theme.border }}
        >
          <div className={styles.status}>
            <h2 className={styles.sectionTitle} style={{ color: theme.text }}>
              Статус
            </h2>
            {statusInfo && (
              <div
                className={styles.statusBadge}
                style={{ backgroundColor: statusInfo.backgroundColor }}
              >
                <span
                  className={styles.statusText}
                  style={{ color: statusInfo.textColor }}
                >
                  {statusInfo.text}
                </span>
              </div>
            )}
          </div>
          {order.createdAt && (
            <div className={styles.infoRow}>
              <span
                className={styles.infoLabel}
                style={{ color: theme.textSecondary }}
              >
                Дата создания:
              </span>
              <span className={styles.infoValue} style={{ color: theme.text }}>
                {format(new Date(order.createdAt), "dd.MM.yyyy HH:mm", {
                  locale: ru,
                })}
              </span>
            </div>
          )}
          {order.updatedAt && (
            <div className={styles.infoRow}>
              <span
                className={styles.infoLabel}
                style={{ color: theme.textSecondary }}
              >
                Последнее обновление:
              </span>
              <span className={styles.infoValue} style={{ color: theme.text }}>
                {format(new Date(order.updatedAt), "dd.MM.yyyy HH:mm", {
                  locale: ru,
                })}
              </span>
            </div>
          )}
        </div>

        {order.items && order.items.length > 0 && (
          <div
            className={styles.section}
            style={{ backgroundColor: theme.card, borderColor: theme.border }}
          >
            <h2 className={styles.sectionTitle} style={{ color: theme.text }}>
              Товары
            </h2>
            <div className={styles.itemsTable}>
              <div
                className={styles.tableHeader}
                style={{ borderBottomColor: theme.border }}
              >
                <span
                  className={styles.tableHeaderText}
                  style={{ flex: 2, color: theme.primary }}
                >
                  Название
                </span>
                <span
                  className={styles.tableHeaderText}
                  style={{ flex: 1, textAlign: "center", color: theme.primary }}
                >
                  Кол-во
                </span>
                <span
                  className={styles.tableHeaderText}
                  style={{ flex: 1, textAlign: "right", color: theme.primary }}
                >
                  Цена
                </span>
                <span
                  className={styles.tableHeaderText}
                  style={{ flex: 1, textAlign: "right", color: theme.primary }}
                >
                  Сумма
                </span>
              </div>
              {order.items.map((item, index) => (
                <div
                  key={index}
                  className={styles.tableRow}
                  style={{ borderBottomColor: `${theme.border}20` }}
                >
                  <span
                    className={styles.itemName}
                    style={{ color: theme.text, flex: 2 }}
                  >
                    {item.name}
                  </span>
                  <span
                    className={styles.itemQuantity}
                    style={{ color: theme.text, flex: 1, textAlign: "center" }}
                  >
                    {item.quantity}
                  </span>
                  <span
                    className={styles.itemPrice}
                    style={{ color: theme.text, flex: 1, textAlign: "right" }}
                  >
                    {item.basePrice > 0
                      ? `${Number(item.basePrice).toLocaleString("ru-RU")} ₽`
                      : "—"}
                  </span>
                  <span
                    className={styles.itemTotal}
                    style={{ color: theme.text, flex: 1, textAlign: "right" }}
                  >
                    {item.total > 0
                      ? `${Number(item.total).toLocaleString("ru-RU")} ₽`
                      : "—"}
                  </span>
                </div>
              ))}
            </div>
            <div
              className={styles.totalRow}
              style={{ borderTopColor: theme.primary }}
            >
              <span className={styles.totalLabel} style={{ color: theme.text }}>
                Итого
              </span>
              <span
                className={styles.totalValue}
                style={{ color: theme.primary }}
              >
                {Number(order.total).toLocaleString("ru-RU")} ₽
              </span>
            </div>
          </div>
        )}

        {order.data && order.data.length > 0 && (
          <div
            className={styles.section}
            style={{ backgroundColor: theme.card, borderColor: theme.border }}
          >
            <h2 className={styles.sectionTitle} style={{ color: theme.text }}>
              Детали заказа
            </h2>
            {order.data.map((item, index) => (
              <div key={index} className={styles.infoRow}>
                <span
                  className={styles.infoLabel}
                  style={{ color: theme.textSecondary }}
                >
                  {getFieldName(item.fieldTemplateId)}:
                </span>
                <span
                  className={styles.infoValue}
                  style={{ color: theme.text }}
                >
                  {item.value === "true"
                    ? "Да"
                    : item.value === "false"
                      ? "Нет"
                      : item.value}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className={styles.desktopActionsContainer}>
        <button className={styles.repeatButton} onClick={handleRepeat}>
          <MdRepeat size={24} color={theme.onPrimary} />
          <span className={styles.repeatButtonText}>Повторить заявку</span>
        </button>
      </div>
    </div>
  );
}
